/**
 * Where DOM focus sits in the body, kept as the root's `data-focus-level`:
 * the focused row draws its border only at `row`, the active cell its
 * outline at `cell`, and at `control` (an editor, a toggle, a button in a
 * cell) only the control's own ring shows.
 */
export type FocusLevel = "row" | "cell" | "control";

/** The level of a focus target inside the grid body, or `null` outside the rows. */
export const getFocusLevel = (
    target: EventTarget | null,
    container: Element,
): FocusLevel | null => {
    if (!(target instanceof Element) || !container.contains(target)) {
        return null;
    }
    if (target.hasAttribute("data-row-id")) {
        return "row";
    }
    if (target.getAttribute("role") === "gridcell") {
        return "cell";
    }
    return target.closest("[data-row-id]") === null ? null : "control";
};

/** Controls inside a cell that can take focus, roving `tabindex="-1"` ones included. */
export const FOCUSABLE_SELECTOR =
    "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]";

const BUTTON_LIKE_SELECTOR = "button, a[href], [role='button']";

/** Whether `target` sits in a cell's controls (`[data-dt-controls]`, `ActionsCell`). */
export const isInsideCellControls = (target: EventTarget | null) =>
    target instanceof Element && target.closest("[data-dt-controls]") !== null;

/** Kinds that keep focus on the cell even with one control in it. */
const CELL_FOCUS_KINDS = new Set(["select", "expand", "dragHandle"]);

/**
 * The one button-like control in a cell the grid navigates to, which takes
 * focus in the cell's place (WAI-ARIA grid: a cell holding a single widget
 * focuses the widget). Only controls in `[data-dt-controls]` (`ActionsCell`)
 * count, so a value with a control beside it (a title and its toggle) keeps
 * focus on the cell. `null` for any other cell.
 */
export const getSoleControl = (cell: Element): HTMLElement | null => {
    if (
        cell.getAttribute("role") !== "gridcell" ||
        !cell.hasAttribute("tabindex") ||
        CELL_FOCUS_KINDS.has(cell.getAttribute("data-kind") ?? "")
    ) {
        return null;
    }
    const focusables = cell.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const control = focusables.length === 1 ? focusables[0] : undefined;
    return control?.matches(BUTTON_LIKE_SELECTOR) &&
        isInsideCellControls(control)
        ? control
        : null;
};

/**
 * Focus that landed on a one-control cell (Tab, a click) moves on to its
 * control. Shift+Tab back from the control (`from`) stays on the cell, so
 * the control can still be left. Returns whether focus moved.
 */
export const handFocusToSoleControl = (
    target: EventTarget,
    from: EventTarget | null,
) => {
    const control = target instanceof Element ? getSoleControl(target) : null;
    if (control === null || (from instanceof Node && control.contains(from))) {
        return false;
    }
    control.focus({ preventScroll: true });
    return true;
};

/** Whether `target` is the control `getSoleControl` focuses in its cell's place. */
export const isSoleControl = (target: EventTarget | null) => {
    const cell =
        target instanceof Element ? target.closest("[role='gridcell']") : null;
    return cell !== null && getSoleControl(cell) === target;
};
